import React, { useState } from "react";
import { Box, useTheme } from "@mui/material";
import Button from "../../components/Button";
import AddIcon from "@mui/icons-material/Add";
import { pushErrorMessage } from '../../utils/notifications';
import UsersDataGrid from "./components/usersDataGrid";
import Popup from '../../components/Popup';
import { FormattedMessage } from "react-intl";
import messages from "./components/messages";
import AddEditUserDialog from "./components/addEditUserDialog";
import { tokens } from "../../assets/theme";
import { useQuery } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";
import { useMsal, useAccount } from "@azure/msal-react";
import AxionControls from "../../components/controls/AxionControls";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { fetchUsers, updateUser, createUser, deleteUser } from "../../fetchers/user";
import { fetchRoles } from "../../fetchers/role";
import { fetchDepartments } from "../../fetchers/cmmsAssetHierarchy";
import { fetchUserDepartments, replaceUserDepartments } from "../../fetchers/cmmsUserDepartment";
import { useAuth } from "../../auth/AuthContext";
import { hasAnyRole } from "../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../cmms/cmmsRoles";
import { useIsCmmsEnabled } from "../cmms/cmmsFeature";
import { useIntl } from "react-intl";

const UsersIndex = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [openPopup, setOpenPopup] = useState(false);
    const [user, setUser] = useState({
        id: null,
        username: "",
        name: "",
        email: "",
        roles: [],
        departments: []
    });
    const [showBackdrop, setShowBackdrop] = useState(false);
    const intl = useIntl();

    const usersQuery = useQuery(["users"], () => fetchUsers(),
        {
            keepPreviousData: true
        }
    );

    const rolesQuery = useQuery(["roles"], () => fetchRoles(),
        {
            keepPreviousData: true
        }
    );
    const { roleIds } = useAuth();

    // The department endpoints are Cmms_Manager-gated, so anyone else would only get a 403
    // out of this query. Skip it rather than show a section that cannot be saved.
    // The CMMS module switch applies too: user administration is an OEE page, and
    // maintenance departments have no meaning where the module is not switched on.
    const cmmsEnabled = useIsCmmsEnabled();
    const canManageDepartments = cmmsEnabled && hasAnyRole(roleIds, CMMS_MANAGER_ROLES);

    const departmentsQuery = useQuery(
        ["cmmsDepartments", companyKey],
        () => fetchDepartments(companyKey),
        {
            enabled: canManageDepartments,
            keepPreviousData: true
        }
    );

    const handleAddUserClick = () => {
        setUser({
            id: null,
            username: "",
            name: "",
            email: "",
            roles: [],
            departments: [],
        });
        setOpenPopup(true);
    }

    const handleEditUserClick = (id) => {
        const selected = usersQuery.data.find((rowData) => rowData.id === id);

        // departments stays null until the fetch lands. null means "unknown", and handleSave
        // skips the department write while it is null — otherwise a failed or still-pending
        // load would look like an empty selection and wipe the user's real membership.
        setUser({ ...selected, departments: canManageDepartments ? null : [] });
        setOpenPopup(true);

        if (!canManageDepartments) return;

        fetchUserDepartments(id, companyKey)
            .then((assigned) => {
                const ids = (assigned ?? []).map((d) => d.assetNodeId);
                // Guard against a slow response landing after the admin switched rows.
                setUser((prev) => (prev.id === id ? { ...prev, departments: ids } : prev));
            })
            .catch((error) => {
                console.log(error);
                pushErrorMessage(intl.formatMessage(messages.departmentsLoadError));
            });
    }

    const handleDeleteUserClick = (id) => {
        handleDelete(id);
    }

    const handleChange = (event) => {
        const { id, name, value, type, checked } = event.target
        setUser(prev => {
            return {
                ...prev,
                [id ? id : name]: type === "checkbox" ? checked : value
            }
        })
    }

    const handleRolesChange = (event) => {
        const { value } = event.target;
        setUser(prev => ({
            ...prev,
            roles: value
        }));
    }

    const handleDepartmentsChange = (event) => {
        const { value } = event.target;
        setUser(prev => ({
            ...prev,
            departments: value
        }));
    }

    /*
     * Departments live behind their own Cmms_Manager-gated endpoint, so they are a second
     * call rather than a field on the user payload.
     *
     * Returns false on failure instead of throwing: the caller reports the two writes
     * separately, so a department failure must not unwind the user save.
     */
    const saveDepartments = async (userId) => {
        if (!canManageDepartments || !Array.isArray(user.departments)) {
            return true;
        }

        try {
            await replaceUserDepartments(userId, companyKey, user.departments);
            return true;
        } catch (error) {
            console.log(error);
            pushErrorMessage(intl.formatMessage(messages.departmentsSaveError));
            return false;
        }
    }

    /*
     * The user record and its departments are two resources behind two different policies:
     * PUT /api/users is Role_Superuser (RoleRequirement(2) — role 2 only, NOT admin), while
     * the department endpoints are Cmms_Manager (role 1). An admin without role 2 is
     * therefore allowed to change departments but not the user itself.
     *
     * So for an existing user the two writes run independently and are reported separately.
     * Chaining them meant a 403 on the user PUT silently discarded a department change the
     * admin had every right to make.
     *
     * A new user still has to chain — departments need the id that only create returns.
     */
    const handleSave = async () => {
        if(user.roles.includes(1) && !hasAnyRole(roleIds, [1])) {
            pushErrorMessage("Only users in Admin role can assign users to Admin role.");
            return;
        }
        setShowBackdrop(true);

        if (user.id) {
            const [userSave, departmentsOk] = await Promise.all([
                updateUser(user).then(() => true).catch((error) => {
                    console.log(error);
                    pushErrorMessage(`Cannot update the user.`);
                    return false;
                }),
                saveDepartments(user.id),
            ]);

            setShowBackdrop(false);
            usersQuery.refetch();
            // Keep the dialog open if anything failed, so the admin can retry or copy out
            // what they typed rather than losing it to a closing popup.
            if (userSave && departmentsOk) setOpenPopup(false);
            return;
        }

        try {
            const created = await createUser(user);
            const departmentsOk = await saveDepartments(created.id);
            usersQuery.refetch();
            setShowBackdrop(false);
            if (departmentsOk) setOpenPopup(false);
        } catch (error) {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Cannot create the user.`);
        }
    }

    const handleDelete = (id) => {
        setShowBackdrop(true);
        deleteUser(id).then(() => {
            usersQuery.refetch();
            setShowBackdrop(false);
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot delete the user.`);
            });
    }

    if (usersQuery.isLoading || rolesQuery.isLoading)
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress color="secondary" /></div>;

    return (
        <Box maxWidth={1800} >
            <Button
                text={<FormattedMessage {...messages.addUserLabel} />}
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddUserClick}
                sx={{
                    "&:hover": {
                        backgroundColor: colors.dashbordGreen[700],
                    },
                    backgroundColor: colors.dashbordGreen[500],
                    color: "#000000",
                    position: "absolute",
                    right: "10px",
                }} />
            <UsersDataGrid
                users={usersQuery}
                roles={rolesQuery.data}
                handleDeleteUserClick={handleDeleteUserClick}
                handleEditUserClick={handleEditUserClick}
                isPopupOpen={openPopup} />
            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidht="sm"
                title={user.id != null ? <FormattedMessage {...messages.editUserLabel} /> : <FormattedMessage {...messages.addUserLabel} />}>
                <AddEditUserDialog
                    onChange={handleChange}
                    onRolesChange={handleRolesChange}
                    onDepartmentsChange={handleDepartmentsChange}
                    handleSave={handleSave}
                    user={user}
                    roles={rolesQuery.data}
                    departments={departmentsQuery.data}
                    canManageDepartments={canManageDepartments}
                    setOpenPopup={setOpenPopup} />
            </Popup>
            <AxionControls.AxionBackdrop open={showBackdrop} />
        </Box>
    );
};

export default UsersIndex;
